/**
 * Phase 45.1b — typed wrappers for the enterprise SSO surface.
 *
 * Public login-flow endpoints live at `/auth/sso/*` (no auth); the admin
 * configuration surface lives at `/orgs/:orgId/sso` (JWT + the
 * `auth.configure_sso` permission). Note the IdP redirect itself
 * (`GET /auth/sso/connections/:id/start`) is a BROWSER navigation, not an
 * XHR — consumers build that URL themselves and `window.location.assign`
 * it, so there is intentionally no wrapper for it here.
 */
import type { AxiosInstance } from 'axios';
import type {
  CreateScimTokenRequest,
  CreateScimTokenResponse,
  DeleteSsoConnectionResponse,
  RevokeScimTokenResponse,
  ScimTokenListResponse,
  SsoConnectionResponse,
  SsoDiscoverResponse,
  SsoExchangeResponse,
  SsoNativeExchangeRequest,
  LoginResponse,
  SsoTrustView,
  UpdateSsoTrustDelegatesRequest,
  UpsertSsoConnectionRequest,
} from '@commerce-os/shared-types';

/** Login-page IdP discovery — domain-level, no user enumeration. */
export async function discoverSso(
  client: AxiosInstance,
  email: string,
): Promise<SsoDiscoverResponse> {
  const { data } = await client.get<SsoDiscoverResponse>('/auth/sso/discover', {
    params: { email },
  });
  return data;
}

/** One-time exchange code → normal token pair (single-use, 60s TTL). */
export async function exchangeSsoCode(
  client: AxiosInstance,
  token: string,
): Promise<SsoExchangeResponse> {
  const { data } = await client.post<SsoExchangeResponse>('/auth/sso/exchange', {
    token,
  });
  return data;
}

export async function exchangeNativeSsoCode(client: AxiosInstance, input: SsoNativeExchangeRequest): Promise<LoginResponse> {
  const { data } = await client.post<LoginResponse>('/auth/sso/native/exchange', input);
  return data;
}

export async function getSsoConnection(
  client: AxiosInstance,
  orgId: string,
): Promise<SsoConnectionResponse> {
  const { data } = await client.get<SsoConnectionResponse>(`/orgs/${orgId}/sso`);
  return data;
}

/** Create-or-update; resolves to the same envelope as the GET. */
export async function upsertSsoConnection(
  client: AxiosInstance,
  orgId: string,
  body: UpsertSsoConnectionRequest,
): Promise<SsoConnectionResponse> {
  const { data } = await client.put<SsoConnectionResponse>(
    `/orgs/${orgId}/sso`,
    body,
  );
  return data;
}

export async function deleteSsoConnection(
  client: AxiosInstance,
  orgId: string,
): Promise<DeleteSsoConnectionResponse> {
  const { data } = await client.delete<DeleteSsoConnectionResponse>(
    `/orgs/${orgId}/sso`,
  );
  return data;
}

/** Phase 67 hotfix (question 116) — the full list of people an owner lets
 *  change the SSO trust settings. Only an owner or a platform admin may. */
export async function updateSsoTrustDelegates(
  client: AxiosInstance,
  orgId: string,
  body: UpdateSsoTrustDelegatesRequest,
): Promise<SsoTrustView> {
  const { data } = await client.put<SsoTrustView>(
    `/orgs/${orgId}/sso/trust-delegates`,
    body,
  );
  return data;
}

// ── SCIM provisioning tokens (Phase 45.2b) ────────────────────────────────

/** Mint a SCIM bearer token — the response's `token` is the raw secret,
 *  shown exactly once and never retrievable again. */
export async function createScimToken(
  client: AxiosInstance,
  orgId: string,
  body: CreateScimTokenRequest = {},
): Promise<CreateScimTokenResponse> {
  const { data } = await client.post<CreateScimTokenResponse>(
    `/orgs/${orgId}/sso/scim-tokens`,
    body,
  );
  return data;
}

export async function listScimTokens(
  client: AxiosInstance,
  orgId: string,
): Promise<ScimTokenListResponse> {
  const { data } = await client.get<ScimTokenListResponse>(
    `/orgs/${orgId}/sso/scim-tokens`,
  );
  return data;
}

export async function revokeScimToken(
  client: AxiosInstance,
  orgId: string,
  tokenId: string,
): Promise<RevokeScimTokenResponse> {
  const { data } = await client.delete<RevokeScimTokenResponse>(
    `/orgs/${orgId}/sso/scim-tokens/${tokenId}`,
  );
  return data;
}
